import { useCallback, useRef, useState } from "react";

import { CopilotAction } from "../api";
import { useEphemeralDrafts } from "./useEphemeralDraft";

const newId = (): string => crypto.randomUUID();

export type StreamInput = {
  context?: Record<string, unknown>;
  sessionId: string;
  userText: string;
};

export type StreamEvents = {
  onChunk?: (chunk: string) => void; // token/char chunks
  onComplete?: (final: { actions?: CopilotAction[]; content: string }) => void;
  onError?: (err: unknown) => void;
};

export type StartStreamFn = (args: {
  abortController: AbortController;
  events: StreamEvents;
  input: StreamInput;
  signal: AbortSignal;
}) => Promise<void>;

export type LoadConversationFn = (sessionId: string) => Promise<void>;

export const useAssistantStream = (opts: {
  loadConversation: LoadConversationFn;
  sessionId: string;
  startStream: StartStreamFn;
}): {
  cancel: () => void;
  clearSession: () => void;
  error: Error | null;
  isStreaming: boolean;
  send: (userText: string, context?: Record<string, unknown>) => Promise<void>;
} => {
  const { loadConversation, sessionId, startStream } = opts;

  const controllerRef = useRef<AbortController | null>(null);
  const currentDraftIdRef = useRef<null | string>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { addDraft, clearDraft, clearSessionDrafts, updateDraft } =
    useEphemeralDrafts();

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
    setIsStreaming(false);
    setError(null);

    // Mark the current assistant draft as partial to show "Response stopped by user"
    if (currentDraftIdRef.current) {
      updateDraft(currentDraftIdRef.current, {
        isPartial: true,
      });
      currentDraftIdRef.current = null;
    }
  }, [updateDraft]);

  const send = useCallback(
    async (userText: string, context?: Record<string, unknown>) => {
      controllerRef.current?.abort();

      // Clear any previous ephemeral drafts when starting a new message
      // This ensures cancelled messages are cleared when the user performs another action
      if (sessionId) {
        clearSessionDrafts(sessionId);
        void loadConversation(sessionId);
      }

      setError(null);
      setIsStreaming(true);

      // Persist user message
      const userMessageId = newId();
      addDraft({
        content: userText,
        createdAt: Date.now().toString(),
        isPartial: false,
        messageId: userMessageId,
        role: "user",
        sessionId,
      });

      // Create an ephemeral draft to render streaming tokens
      const draftId = newId();
      currentDraftIdRef.current = draftId;
      addDraft({
        content: "",
        createdAt: Date.now().toString(),
        isPartial: false,
        messageId: draftId,
        role: "assistant",
        sessionId,
      });

      // Start stream with abort semantics
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        await startStream({
          abortController: controller,
          events: {
            onChunk: (chunk: string) => {
              updateDraft(draftId, (curr) => ({
                content: curr.content + chunk,
              }));
            },
            onComplete: async () => {
              try {
                // Clear the ephemeral draft first so the UI doesn't flicker
                currentDraftIdRef.current = null;
                clearSessionDrafts(sessionId);
                await loadConversation(sessionId);
              } finally {
                setIsStreaming(false);
              }
            },
            onError: async (err) => {
              if (err instanceof Error && err.name === "AbortError") {
                // Aborted, don't set error state
                // Note: The cancel function will handle marking the draft as partial
                return;
              }
              currentDraftIdRef.current = null;
              const errorObj =
                err instanceof Error ? err : new Error(String(err));
              setError(errorObj);
              setIsStreaming(false);
            },
          },
          input: { context, sessionId, userText },
          signal: controller.signal,
        });
      } catch (e) {
        try {
          currentDraftIdRef.current = null;
          clearSessionDrafts(sessionId);
          await loadConversation(sessionId);
        } finally {
          setIsStreaming(false);
        }
        // If aborted, swallow; otherwise surface
        if (e instanceof Error && e.name !== "AbortError") {
          setError(e);
        } else if (!(e instanceof Error) || e.name !== "AbortError") {
          const errorObj = new Error(String(e));
          setError(errorObj);
        }
      }
    },
    [
      sessionId,
      startStream,
      loadConversation,
      addDraft,
      clearDraft,
      clearSessionDrafts,
      updateDraft,
    ],
  );

  // clear all ephemeral drafts for this session
  const clearSession = useCallback(() => {
    clearSessionDrafts(sessionId);
  }, [sessionId, clearSessionDrafts]);

  return {
    cancel,
    clearSession,
    error,
    isStreaming,
    send,
  };
};
